// Central content source for the Amden People page.
// Structured so a future website admin area can manage every field
// without touching page components.

export const PEOPLE_SEO = {
  title: 'HR, Recruitment & People Development Services | Amden Group',
  description:
    'Build stronger teams with practical HR consultancy, recruitment support, Everything DiSC® profiles, team training and leadership development from Amden Group.',
};

export const PEOPLE_HERO = {
  eyebrow: 'Amden People',
  headingLines: ['Build strong teams.', 'Drive performance.', 'Develop your people.'],
  accentLineIndex: 2,
  body: 'Amden helps businesses recruit, manage and develop their people through practical HR support, structured recruitment, team development and leadership training.',
  primaryCta: {
    label: 'Discuss Your People Needs',
    href: '/?service=people#solution-finder',
  },
  secondaryCta: {
    label: 'Explore People Services',
    target: '#people-services',
  },
  image: {
    src: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1200&q=80',
    alt: 'Facilitated team workshop with colleagues collaborating around a table',
    width: 1200,
    height: 900,
  },
  floatingCard: {
    icon: 'users',
    label: 'People first',
    text: 'Practical HR, recruitment and development support that fits your business.',
  },
};

export const PEOPLE_WHAT_WHO = {
  whatHeading: 'What We Do',
  whatCopy:
    'From recruitment and onboarding to performance, retention and leadership development, Amden provides practical people solutions that help businesses build stronger teams and more consistent management practices.',
  whatAreas: [
    'HR consultancy and ongoing HR support',
    'Recruitment and talent acquisition',
    'HR policies, procedures and documentation',
    'Employment contracts and employee handbooks',
    'Onboarding and probation structures',
    'Performance-management frameworks',
    'Employee engagement and retention',
    'Team development programmes',
    'Leadership and management training',
    'Everything DiSC® profiles and facilitated workshops',
    'Organisational structure and role design',
    'Workplace coaching and people-development support',
  ],
  whoHeading: 'Who It’s For',
  whoCopy:
    'Our people services support businesses that want to strengthen their HR foundations, improve management capability and create teams that can perform and grow.',
  whoAreas: [
    'Growing businesses needing structured HR support',
    'Organisations without internal HR expertise',
    'Companies experiencing recruitment or retention challenges',
    'Teams requiring clearer roles and accountability',
    'Managers needing practical leadership development',
    'Businesses introducing formal people processes',
    'Organisations experiencing change or rapid growth',
    'Employers seeking external HR and recruitment support',
  ],
};

export const PEOPLE_PROBLEMS = {
  eyebrow: 'Problems We Solve',
  heading: 'Problems We Solve',
  intro:
    'People problems rarely exist in isolation. Recruitment, management, communication, performance and retention are closely connected. We help identify the underlying issues and put practical structures in place.',
  items: [
    {
      id: 'hiring-challenges',
      icon: 'user-search',
      title: 'Hiring Challenges',
      description: 'Difficulty attracting, assessing or securing suitable employees.',
      howWeHelp:
        'We structure the whole hiring process — role definition, advertising, screening and selection — so you secure the right people with confidence.',
    },
    {
      id: 'missing-hr-structure',
      icon: 'file-check',
      title: 'Missing HR Structure',
      description: 'Policies, contracts, records and people processes are incomplete, inconsistent or unclear.',
      howWeHelp:
        'We put the foundations in place: clear contracts, practical policies, organised records and people processes that managers can actually follow.',
    },
    {
      id: 'weak-onboarding',
      icon: 'door-open',
      title: 'Weak Onboarding',
      description: 'New employees do not receive the structure, training or support needed to settle and perform.',
      howWeHelp:
        'We design onboarding and probation structures that help new starters settle quickly, understand expectations and perform sooner.',
    },
    {
      id: 'management-capability',
      icon: 'graduation-cap',
      title: 'Management Capability',
      description: 'Managers need practical skills and confidence to lead people consistently.',
      howWeHelp:
        'We develop managers through practical training and coaching — giving them the skills and confidence to lead consistently, not just occasionally.',
    },
    {
      id: 'team-performance',
      icon: 'users',
      title: 'Team Performance',
      description: 'Employees are busy, but roles, expectations and accountability are not sufficiently clear.',
      howWeHelp:
        'We clarify roles, expectations and accountability, then support managers with simple performance frameworks that keep teams on track.',
    },
    {
      id: 'employee-retention',
      icon: 'heart-handshake',
      title: 'Employee Retention',
      description: 'Valuable employees are disengaging or leaving, increasing cost and operational disruption.',
      howWeHelp:
        'We identify what is driving disengagement and strengthen the structures — development, communication, recognition — that keep good people.',
    },
    {
      id: 'difficult-conversations',
      icon: 'message-square',
      title: 'Difficult Conversations',
      description: 'Managers avoid or struggle with performance, conduct, absence or workplace issues.',
      howWeHelp:
        'We equip managers with clear frameworks and practical support so difficult conversations happen early, fairly and constructively.',
    },
    {
      id: 'team-dynamics',
      icon: 'messages-square',
      title: 'Communication and Team Dynamics',
      description: 'Different working styles or unclear communication are creating friction and misunderstandings.',
      howWeHelp:
        'Using tools such as Everything DiSC® profiles and facilitated sessions, we help teams understand working styles and communicate more effectively.',
    },
    {
      id: 'leadership-development',
      icon: 'award',
      title: 'Leadership Development',
      description: 'Current or emerging leaders need structured development to support business growth.',
      howWeHelp:
        'We build structured development for current and emerging leaders, aligned to where your business is going — not generic theory.',
    },
    {
      id: 'organisational-change',
      icon: 'milestone',
      title: 'Organisational Change',
      description: 'Teams need clarity, communication and support during growth, restructuring or operational change.',
      howWeHelp:
        'We support teams through growth and change with clear communication, role clarity and practical people structures that reduce uncertainty.',
    },
  ],
};

/**
 * People services & packages — data-driven and future-purchase-ready.
 *
 * DEVELOPER NOTE (future e-commerce):
 * - `slug` + `href` are the stable identifiers for future service-detail pages.
 * - `purchaseMode` supports: direct_purchase | request_quote | book_consultation | contact_us
 * - `purchasable` stays false and `price` / `vat` stay null until pricing,
 *   terms and Stripe products are approved by Amden Group.
 * - `stripeProductId` / `stripePriceId` are reserved for the approved Stripe catalogue.
 * - `fullDescription`, `format`, `duration`, `included` and `audience` are
 *   intentionally empty until approved content is supplied — do not invent them.
 */
export const PEOPLE_SERVICES = [
  {
    slug: 'hr-packs',
    name: 'HR Packs',
    category: 'HR Documentation',
    shortDescription:
      'Practical HR documentation and essential people-process packages designed around the needs and stage of your business.',
    fullDescription: null,
    icon: 'folder-check',
    featuredImage: null,
    format: null,
    duration: null,
    included: [],
    audience: null,
    price: null,
    vat: null,
    purchaseMode: 'request_quote',
    purchasable: false,
    stripeProductId: null,
    stripePriceId: null,
    href: '/people/hr-packs',
    cta: 'details',
    availability: 'available',
    displayOrder: 1,
  },
  {
    slug: 'everything-disc',
    name: 'Everything DiSC® Profiles',
    category: 'Behavioural Profiles',
    shortDescription:
      'Individual behavioural profiles and facilitated sessions that help people understand communication preferences, working styles and team dynamics.',
    fullDescription: null,
    icon: 'shapes',
    featuredImage: null,
    format: null,
    duration: null,
    included: [],
    audience: null,
    price: null,
    vat: null,
    purchaseMode: 'request_quote',
    purchasable: false,
    stripeProductId: null,
    stripePriceId: null,
    href: '/people/everything-disc',
    cta: 'details',
    availability: 'available',
    displayOrder: 2,
  },
  {
    slug: 'team-training',
    name: 'Team Training',
    category: 'Team Development',
    shortDescription:
      'Practical, facilitated training designed to improve communication, collaboration, accountability and team performance.',
    fullDescription: null,
    icon: 'users',
    featuredImage: null,
    format: null,
    duration: null,
    included: [],
    audience: null,
    price: null,
    vat: null,
    purchaseMode: 'request_quote',
    purchasable: false,
    stripeProductId: null,
    stripePriceId: null,
    href: '/people/team-training',
    cta: 'details',
    availability: 'available',
    displayOrder: 3,
  },
  {
    slug: 'leadership-management-training',
    name: 'Leadership and Management Training',
    category: 'Leadership Development',
    shortDescription:
      'Development programmes that help managers lead confidently, communicate clearly and manage performance effectively.',
    fullDescription: null,
    icon: 'graduation-cap',
    featuredImage: null,
    format: null,
    duration: null,
    included: [],
    audience: null,
    price: null,
    vat: null,
    purchaseMode: 'request_quote',
    purchasable: false,
    stripeProductId: null,
    stripePriceId: null,
    href: '/people/leadership-management-training',
    cta: 'details',
    availability: 'available',
    displayOrder: 4,
  },
  {
    slug: 'recruitment-support',
    name: 'Recruitment Support',
    category: 'Talent Acquisition',
    shortDescription:
      'Flexible recruitment support covering role definition, advertising, screening, selection and onboarding.',
    fullDescription: null,
    icon: 'user-search',
    featuredImage: null,
    format: null,
    duration: null,
    included: [],
    audience: null,
    price: null,
    vat: null,
    purchaseMode: 'book_consultation',
    purchasable: false,
    stripeProductId: null,
    stripePriceId: null,
    href: '/people/recruitment-support',
    cta: 'discuss',
    availability: 'available',
    displayOrder: 5,
  },
  {
    slug: 'hr-consultancy',
    name: 'HR Consultancy',
    category: 'HR Consultancy',
    shortDescription:
      'Practical advice and implementation support for employee relations, HR processes, documentation and workplace challenges.',
    fullDescription: null,
    icon: 'briefcase',
    featuredImage: null,
    format: null,
    duration: null,
    included: [],
    audience: null,
    price: null,
    vat: null,
    purchaseMode: 'book_consultation',
    purchasable: false,
    stripeProductId: null,
    stripePriceId: null,
    href: '/people/hr-consultancy',
    cta: 'discuss',
    availability: 'available',
    displayOrder: 6,
  },
  {
    slug: 'ongoing-hr-support',
    name: 'Ongoing HR Support',
    category: 'HR Consultancy',
    shortDescription:
      'Structured external HR support for businesses that require regular guidance without building a full internal HR department.',
    fullDescription: null,
    icon: 'calendar-check',
    featuredImage: null,
    format: null,
    duration: null,
    included: [],
    audience: null,
    price: null,
    vat: null,
    purchaseMode: 'contact_us',
    purchasable: false,
    stripeProductId: null,
    stripePriceId: null,
    href: '/people/ongoing-hr-support',
    cta: 'discuss',
    availability: 'available',
    displayOrder: 7,
  },
  {
    slug: 'people-organisational-development',
    name: 'People and Organisational Development',
    category: 'Organisational Development',
    shortDescription:
      'Tailored support for organisational structure, role clarity, engagement, performance and change.',
    fullDescription: null,
    icon: 'network',
    featuredImage: null,
    format: null,
    duration: null,
    included: [],
    audience: null,
    price: null,
    vat: null,
    purchaseMode: 'contact_us',
    purchasable: false,
    stripeProductId: null,
    stripePriceId: null,
    href: '/people/people-organisational-development',
    cta: 'discuss',
    availability: 'available',
    displayOrder: 8,
  },
];

export const PEOPLE_SERVICES_SECTION = {
  eyebrow: 'People Services',
  heading: 'Practical support for every stage of the employee journey',
  intro:
    'Choose a defined service or package, or speak with Amden about ongoing or tailored support for your business.',
};

export const PEOPLE_DELIVERY = {
  eyebrow: 'How We Deliver',
  heading: 'A structured route to stronger people practices',
  stages: [
    {
      number: '01',
      title: 'Assessment',
      text: 'We review your current people practices, business needs and immediate challenges to identify gaps and priorities.',
    },
    {
      number: '02',
      title: 'Strategy Development',
      text: 'We create a practical plan covering the right structures, services and expected outcomes.',
    },
    {
      number: '03',
      title: 'Implementation',
      text: 'We work alongside your team to introduce the required policies, processes, recruitment activity, training or development.',
    },
    {
      number: '04',
      title: 'Ongoing Support',
      text: 'We provide continued guidance, review progress and help embed stronger people practices.',
    },
  ],
  diagramLabel:
    'Continuous people-support cycle: assessment, strategy development, implementation, then ongoing support.',
};

export const PEOPLE_CASE_STUDY = {
  eyebrow: 'Case Study',
  heading: 'Kemmlit Ireland – HR & Recruitment Support',
  intro:
    'Ongoing, practical HR and recruitment support embedded with the business — not a one-off placement service.',
  challenge: {
    title: 'Challenge',
    text: 'Kemmlit Ireland required stronger recruitment structure, clearer onboarding processes, and practical HR support as the business continued to grow. Internal management teams were spending increasing amounts of time managing recruitment, onboarding, and employee processes.',
  },
  solution: {
    title: 'Solution',
    text: 'Amden People provided ongoing HR advice, recruitment support, and onboarding improvements designed to strengthen internal structure, improve hiring consistency, and support long-term workforce growth across the business.',
  },
  results: {
    title: 'Results',
    points: [
      'Improved recruitment structure and visibility',
      'Stronger onboarding process',
      'Reduced pressure on internal management',
    ],
  },
  cta: { label: 'Read Full Case Study', href: '/case-studies/kemmlit-ireland' },
};

export const PEOPLE_CTA = {
  index: '06',
  eyebrow: 'Next step',
  heading: 'Ready to strengthen your people and your business?',
  copy: 'Let’s discuss the HR, recruitment, management or team-development support that would make the greatest difference.',
  primaryLabel: 'Discuss Your People Needs',
  primaryHref: '/?service=people#solution-finder',
  secondaryLabel: 'Explore People Services',
  secondaryHref: '#people-services',
};
